import type { HandbookChapter } from '../model';

/** Alles, was am Ticket geschieht -- das Kernstück der täglichen Arbeit. */

const grundlagen: HandbookChapter = {
  id: 'ticket-grundlagen',
  title: 'Das Ticket verstehen',
  summary: 'Nummer, Status, Typ, Priorität, Warteschlange — was die Felder bedeuten.',
  audience: 'agent',
  route: '/app/tickets',
  sections: [
    {
      id: 'nummer',
      title: 'Nummer und Kennung',
      blocks: [
        {
          kind: 'text',
          text: 'Jedes Ticket trägt eine fortlaufende Nummer im Format T-{Jahr}-{laufende Zahl}, je Mandant und Jahr eigen. Diese Nummer nennen Sie dem Kunden, sie steht im Betreff jeder Mail und ist lückenlos. Über diese Nummer oder den Betreff finden Sie auch das Zielticket — beim Zusammenführen, beim Verknüpfen und beim Unterordnen unter ein übergeordnetes Ticket; ein Teil davon genügt. Daneben hat jedes Ticket eine technische Kennung, die in der Adresszeile steht; die brauchen Sie in der Anwendung nicht.',
        },
      ],
    },
    {
      id: 'status',
      title: 'Status und Statusgruppe',
      blocks: [
        {
          kind: 'text',
          text: 'Der Name eines Status ist frei wählbar — „In Bearbeitung“, „Beim Dienstleister“, „Wartet auf Ersatzteil“. Seine Gruppe ist es nicht. An der Gruppe hängen die Auswertungen, die SLA-Uhr und die Sichtbarkeit im Portal. Es gibt sechs, und mehr wird es nicht geben.',
        },
        {
          kind: 'terms',
          items: [
            { term: 'Neu', text: 'Eingegangen, noch nicht angefasst.' },
            { term: 'Offen', text: 'In Arbeit. Die SLA-Uhr läuft.' },
            {
              term: 'Wartet auf Kunden',
              text: 'Sie haben zugeliefert und warten auf Antwort. Ob die Uhr dabei anhält, entscheidet der Administrator je Status.',
            },
            {
              term: 'Wartet intern',
              text: 'Sie warten auf einen Dritten — Lieferant, Kollege, Freigabe.',
            },
            {
              term: 'Gelöst',
              text: 'Fachlich erledigt, der Kunde hat es noch nicht bestätigt. Zählt als abgeschlossen.',
            },
            { term: 'Geschlossen', text: 'Endgültig zu.' },
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Warum „Gelöst“ und „Geschlossen“ getrennt sind',
          text: 'Zwischen beiden liegt die Zufriedenheitsumfrage und die Gelegenheit des Kunden, zu widersprechen. Wer sofort schließt, nimmt sich diese Rückmeldung.',
        },
      ],
    },
    {
      id: 'typ-und-prioritaet',
      title: 'Typ und Priorität',
      blocks: [
        {
          kind: 'text',
          text: 'Der Typ folgt ITIL: Störung (incident), Serviceanfrage (service_request), Problem, Änderung (change). Problem und Änderung bestehen als Typ, haben aber bewusst keinen eigenen Prozess — sie sind Etiketten, kein Verfahren.',
        },
        {
          kind: 'text',
          text: 'Die Priorität entsteht nicht aus dem Bauchgefühl, sondern aus zwei Angaben: Auswirkung (wie viele sind betroffen) und Dringlichkeit (wie schnell muss es weg). Die Prioritätsmatrix des Mandanten setzt daraus die Priorität. Sie können sie danach von Hand übersteuern, aber der Vorschlag kommt aus der Matrix — so beurteilen zwei Bearbeiter denselben Fall gleich.',
        },
        {
          kind: 'example',
          title: 'Beispiel',
          text: 'Der Dateiserver einer Niederlassung mit vierzig Leuten steht: Auswirkung „hoch“, Dringlichkeit „hoch“ → Priorität „Kritisch“. Ein einzelner Anwender kann seinen zweiten Bildschirm nicht einrichten: Auswirkung „niedrig“, Dringlichkeit „niedrig“ → Priorität „Niedrig“. An der Priorität hängt die zugesagte Frist.',
        },
        { kind: 'route', to: '/app/administration/priority-matrix', label: 'Zur Prioritätsmatrix' },
      ],
    },
    {
      id: 'zustaendigkeit',
      title: 'Warteschlange, Bearbeiter, Verantwortlicher',
      blocks: [
        {
          kind: 'terms',
          items: [
            {
              term: 'Warteschlange',
              text: 'Die fachliche Zuständigkeit — „Service Desk“, „Netzwerk“, „Buchhaltungssoftware“. An ihr hängen die Eingangsadresse, der Standardbearbeiter, die Signatur und die Geschäftszeiten.',
            },
            {
              term: 'Bearbeiter',
              text: 'Wer daran arbeitet. Ein Ticket ohne Bearbeiter ist unerledigte Arbeit, die niemandem gehört — deshalb der Knopf „Ticket übernehmen“.',
            },
            {
              term: 'Verantwortlich',
              text: 'Wer dafür geradesteht. Meist dieselbe Person. Getrennt wird es, wenn ein Kollege die Hände anlegt und ein anderer den Kunden führt.',
            },
          ],
        },
      ],
    },
    {
      id: 'herkunft',
      title: 'Herkunft',
      blocks: [
        {
          kind: 'text',
          text: 'Woher das Ticket stammt: E-Mail, Portal, Telefon, Web-Formular, Schnittstelle oder Automatisierung. Das Feld wird gesetzt und nicht gepflegt — es sagt bei der Auswertung, über welchen Kanal die Arbeit wirklich hereinkommt.',
        },
      ],
    },
  ],
};

const bearbeiten: HandbookChapter = {
  id: 'ticket-bearbeiten',
  title: 'Tickets bearbeiten',
  summary:
    'Liste, Suche, gespeicherte Ansichten, wer auf uns wartet, Verlauf, Beiträge, Anhänge, Abschluss.',
  audience: 'agent',
  route: '/app/tickets',
  sections: [
    {
      id: 'liste',
      title: 'Die Ticketliste',
      blocks: [
        {
          kind: 'shot',
          file: 'ticket-liste',
          caption: 'Filter oben, gespeicherte Ansichten daneben, die Liste darunter.',
        },
        {
          kind: 'text',
          text: 'Die Liste ist Ihr Arbeitsplatz. Sie lässt sich nach Status, Warteschlange, Bearbeiter, Priorität und Kunde filtern und über die Spaltenköpfe sortieren. Die Liste lädt nachweise — der Knopf „Weitere laden“ am Ende holt die nächste Seite.',
        },
        {
          kind: 'text',
          text: 'Sie öffnet auf dem Reiter „Offen“ — der Frage, mit der man sie üblicherweise aufschlägt. „Alle“ steht am Ende der Reihe: Es zeigt auch Gelöstes und Geschlossenes und ist der Weg, wenn Sie etwas Älteres wiederfinden wollen.',
        },
        {
          kind: 'text',
          text: 'Sortiert wird zunächst nach dem Anlagedatum, das Neueste oben — damit sehen Sie beim Aufschlagen, was hinzugekommen ist. Nach der Änderungszeit rutschte ein neues Ticket nach unten, sobald jemand einen älteren Vorgang anfasst.',
        },
        {
          kind: 'terms',
          items: [
            {
              term: 'Sortierung umstellen',
              text: 'Ein Klick auf eine Spaltenüberschrift sortiert danach, absteigend zuerst. Ein zweiter Klick auf dieselbe Überschrift dreht die Richtung um. Ein Pfeil an der Überschrift zeigt, wonach die Liste gerade geordnet ist.',
            },
            {
              term: 'Welche Spalten sich sortieren lassen',
              text: 'Nummer, Betreff, Angelegt, Geändert und SLA. Kunde, Status, Priorität, Warteschlange, Kategorie und Bearbeiter nicht — ihre Überschriften bleiben deshalb Text und sind nicht anklickbar. Sie stehen als Verweis in der Zeile; eine Sortierung danach ordnete nach internen Kennungen und damit nach nichts, was man erwarten würde.',
            },
            {
              term: 'Sortierung im Lesezeichen',
              text: 'Die Reihenfolge steht wie die Filter in der Adresszeile. Wer eine sortierte Liste weiterschickt, schickt die Sortierung mit.',
            },
          ],
        },
        {
          kind: 'text',
          text: 'Die Volltextsuche oben durchsucht Betreff, Beiträge und Anhangsnamen. Sie sortiert nach Trefferqualität statt nach Datum — bei einer Suche will man den passendsten Treffer oben, nicht den neuesten.',
        },
        {
          kind: 'note',
          tone: 'info',
          title: '„Offen“ und „In Bearbeitung“ sind zweierlei',
          text: 'Der erste Reiter heißt „Offen“ und meint alles, was noch zu tun ist — Neu, In Bearbeitung und beide Wartezustände, also alles außer Gelöst und Geschlossen. Das ist dieselbe Abgrenzung, die die Kachel „Offene Tickets“ auf der Startseite zählt. Der Reiter „In Bearbeitung“ weiter rechts ist enger: Er lässt ein frisch angelegtes Ticket aus. Es ist immer genau ein Reiter gewählt — „offen und geschlossen“ wäre eine leere Liste.',
        },
        {
          kind: 'text',
          text: 'Der Schalter „Meine Tickets“ rechts neben den Reitern zeigt nur, was auf Ihrem Namen liegt. Er ist kein Reiter, sondern grenzt quer zu ihnen ein — meine offenen, meine geschlossenen — und lässt sich mit jedem von ihnen verbinden. Neben der Überschrift steht, wie viele Zeilen geladen sind; ein „+“ dahinter bedeutet, dass noch weitere folgen. Wie viele es insgesamt sind, steht an dem gewählten Reiter.',
        },
        {
          kind: 'terms',
          items: [
            {
              term: 'Die Zahl an jedem Reiter',
              text: 'Jeder Reiter und beide Schalter tragen eine Zahl: so viele Tickets, wie ein Klick darauf zeigen würde. Was sonst eingegrenzt ist — Kunde, Suchbegriff, „Meine Tickets“, „Wartet auf uns“ — gilt für die Zahlen mit. Ist „Meine Tickets“ an, zählt „Offen“ also Ihre offenen Tickets.',
            },
            {
              term: 'Die Zahl an den Schaltern',
              text: '„Wartet auf uns“ und „Meine Tickets“ zählen für den gewählten Reiter: „Wartet auf uns 3“ unter „Offen“ heißt, drei offene Vorgänge warten auf eine Antwort. Wechseln Sie den Reiter, wechselt die Zahl mit.',
            },
            {
              term: 'Auch die Null steht da',
              text: '„Wartet auf uns 0“ ist die gute Nachricht. Steht an einem Reiter keine Zahl, ist sie noch nicht geladen.',
            },
            {
              term: 'Wann die Zahlen sich ändern',
              text: 'Beim Aufschlagen der Liste und nach jeder eigenen Änderung an einem Ticket. Was ein Kollege in der Zwischenzeit ändert, erscheint beim nächsten Aufruf der Liste — die Zahlen holen sich nicht im Hintergrund nach.',
            },
          ],
        },
        {
          kind: 'terms',
          items: [
            {
              term: 'Ganze Zeile anklicken',
              text: 'Ein Klick irgendwo in die Zeile öffnet das Ticket. Die Verweise auf Nummer, Betreff und Kunde bleiben daneben bestehen — sie sind der Weg mit der Tastatur und der Weg in einen neuen Browserreiter. Wer Text in der Zeile markiert, um ihn zu kopieren, löst nichts aus.',
            },
            {
              term: 'Zeilenhöhe',
              text: 'Der Umschalter „Normal/Eng“ über der Liste verringert den Zeilenabstand. Eng zeigt rund ein Drittel mehr Zeilen ohne Blättern. Die Einstellung merkt sich Ihr Browser und gilt für alle Listen — sie wird nicht am Server gespeichert, weil sie zum Arbeitsplatz gehört und nicht zur Person.',
            },
            {
              term: 'Tabellenkopf',
              text: 'Bei langen Listen rollt die Tabelle innerhalb ihrer Fläche, und der Kopf mit den Spaltennamen bleibt dabei stehen — die Filter darüber ebenfalls. Ohne das müsste man ab der siebten Spalte raten, was dort steht.',
            },
          ],
        },
        {
          kind: 'text',
          text: 'Führt Ihr Mandant mehrere Kunden, steht über der Suche das Feld „Kunde“. Wählen Sie dort einen Kunden, zeigt das Ticketmodul nur noch seine Vorgänge — die Liste, die Zahl neben der Überschrift und das Kanban-Board. Die übrigen Filter wirken zusätzlich: „Offen“ bei Kunde X, „Meine Tickets“ bei Kunde X. Ein neues Ticket bekommt den gewählten Kunden vorbelegt, Sie können ihn im Dialog aber ändern. Das Kreuz im Feld nimmt die Auswahl zurück.',
        },
        {
          kind: 'terms',
          items: [
            {
              term: 'Kundenauswahl und gespeicherte Ansichten',
              text: 'Der Kunde ist Ihr Arbeitskontext und nicht Teil einer Ansicht. Eine Ansicht anzuwenden lässt ihn stehen, und eine Ansicht, die Sie bei gewähltem Kunden speichern, bleibt für alle Kunden gültig.',
            },
            {
              term: 'Ruhende Kunden',
              text: 'Auch inaktive Kunden lassen sich wählen — um ihre alten Vorgänge nachzuschlagen. Vorbelegt wird beim Anlegen nur ein aktiver Kunde.',
            },
            {
              term: 'Wie lange die Auswahl gilt',
              text: 'Sie wandert zwischen Liste und Board mit. Wer das Ticketmodul über die Navigation neu aufruft, sieht wieder alle Kunden — eine Einschränkung, die sich still über den Tag hält, lässt Vorgänge der anderen Kunden übersehen.',
            },
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Der Filter steht in der Adresse',
          text: 'Reiter, Sortierung, Suchbegriff, Kunde und „Meine Tickets“ stehen in der Adresszeile. Damit lässt sich eine gefilterte Liste als Lesezeichen ablegen und an einen Kollegen schicken, und die Zurück-Taste des Browsers stellt den vorigen Filter wieder her, statt die Liste zu verlassen. Ihre Benutzerkennung steht dabei nicht in der Adresse — „Meine Tickets“ erscheint dort als „owner=me“ und wird erst in der Anwendung aufgelöst.',
        },
      ],
    },
    {
      id: 'ansichten',
      title: 'Gespeicherte Ansichten',
      blocks: [
        {
          kind: 'text',
          text: 'Eine Ansicht merkt sich Filter, Spalten und Sortierung. Statt jeden Morgen dieselben vier Filter zu setzen, klicken Sie einmal.',
        },
        {
          kind: 'steps',
          steps: [
            { text: 'Filter, Spalten und Sortierung so einstellen, wie Sie sie brauchen.' },
            { text: '„Ansicht speichern“ klicken und einen Namen vergeben.' },
            {
              text: 'Bei Bedarf „Für alle Bearbeiter freigeben“ ankreuzen.',
              detail:
                'Eine geteilte Ansicht gehört weiter Ihnen; nur Sie dürfen sie ändern oder löschen. Sonst wäre eine gemeinsam benutzte Ansicht ein Bestand, den jeder unter den anderen wegziehen kann.',
            },
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Ansicht nachziehen',
          text: '„Ansicht aktualisieren“ übernimmt die aktuellen Filter in eine bestehende Ansicht. Das ist der übliche Weg — Ansichten wachsen mit der Arbeit.',
        },
      ],
    },
    {
      id: 'anlegen',
      title: 'Ein Ticket anlegen',
      blocks: [
        {
          kind: 'shot',
          file: 'ticket-anlegen',
          caption: 'Die Maske „Ticket anlegen“.',
        },
        {
          kind: 'steps',
          steps: [
            { text: 'In der Ticketliste „Neues Ticket“ klicken.' },
            {
              text: 'Betreff eingeben — mindestens drei Zeichen.',
              detail:
                'Der Betreff steht später in jeder Mail und in jeder Liste. „Drucker geht nicht“ hilft niemandem; „Drucker Buchhaltung EG zieht kein Papier ein“ hilft.',
            },
            {
              text: 'Beschreibung ausfüllen: Was ist passiert, seit wann, wer ist betroffen.',
              detail:
                'Derselbe Editor wie am Beitrag — Fett, Aufzählungen, Zitat, Link. Kein Bild: Beim Anlegen gibt es das Ticket noch nicht, an dem ein Anhang hängen könnte; Dateien kommen mit dem ersten Beitrag dazu.',
            },
            {
              text: 'Kunde und Ansprechpartner wählen.',
              detail:
                'Im Feld „Kunde“ genügen ein paar Buchstaben des Namens oder der Kundennummer. Inaktive Kunden erscheinen hier nicht. Daran hängen die Servicezusage, die Kommunikationshistorie und die Auswertung.',
            },
            {
              text: 'Auswirkung und Dringlichkeit setzen — die Priorität ergibt sich daraus.',
            },
            {
              text: 'Anlegen.',
              detail:
                'Sind in den letzten 24 Stunden ähnliche Tickets eingegangen, zeigt die Maske sie als „Möglicherweise schon gemeldet“ an. Sie hindern Sie nicht, und das ist Absicht: Am Montagmorgen melden fünf Leute denselben Ausfall, und jeder hat recht. Zusammenlegen lässt es sich hinterher.',
            },
          ],
        },
      ],
    },
    {
      id: 'kopf',
      title: 'Der Kopf des Tickets',
      blocks: [
        {
          kind: 'text',
          text: 'Unter dem Betreff stehen Priorität, Bearbeiter und Warteschlange als Auswahlfelder. Sie ändern den Wert dort direkt: auswählen genügt, es gibt keinen Speichernknopf. Diese drei ändert man im Tagesgeschäft am häufigsten, und der Weg über den Reiter „Felder“ wären für jede Änderung vier Handgriffe.',
        },
        {
          kind: 'note',
          tone: 'warning',
          title: 'Der Status ist dort nicht dabei — mit Absicht',
          text: 'Ein Ticket wird nicht über ein Auswahlfeld geschlossen, sondern über die Knöpfe „Lösen“, „Schließen“ und „Wieder öffnen“ rechts oben. An einem Zustandswechsel hängen Vorbedingungen, der Protokolleintrag und die SLA-Uhr; ein Auswahlfeld würde all das übergehen.',
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Wenn jemand anders schneller war',
          text: 'Ändert jemand dasselbe Ticket gleichzeitig, lehnt der Server Ihre Änderung ab und meldet das unter der Zeile. Ihr Stand wird nicht stillschweigend über den fremden geschrieben. Laden Sie die Seite neu und sehen Sie sich an, was inzwischen geschehen ist.',
        },
      ],
    },
    {
      id: 'reiter',
      title: 'Die Reiter am Ticket',
      blocks: [
        {
          kind: 'shot',
          file: 'ticket-verlauf',
          caption: 'Das geöffnete Ticket: Verlauf links, die Felder in der Randspalte rechts.',
        },
        {
          kind: 'text',
          text: 'Eine blaue Zahl neben dem Namen eines Reiters sagt, wie viele Einträge darunter stehen — zwei Termine, drei Anhänge, fünf Punkte in der Struktur. Ein Reiter ohne Zahl ist leer. So sehen Sie beim Öffnen des Tickets, wo etwas steht, ohne jeden Reiter anzuklicken. „Felder“ und „Historie“ tragen keine Zahl: Sie sind bei jedem Ticket gefüllt.',
        },
        {
          kind: 'terms',
          items: [
            {
              term: 'Verlauf',
              text: 'Alle Beiträge, der neueste oben, dazu das Schreibfeld. Jeder Beitrag klappt auf; zugeklappt steht seine erste Zeile da.',
            },
            {
              term: 'Anhänge',
              text: 'Alle Dateien des Tickets an einer Stelle, statt sie im Verlauf zu suchen.',
            },
            { term: 'Felder', text: 'Status, Priorität, Zuständigkeit, Kategorie, eigene Felder.' },
            {
              term: 'Termine',
              text: 'Termine an diesem Ticket, auf Wunsch mit Einladung in den Kalender der Mitarbeiter — siehe das Kapitel „Termine“.',
            },
            {
              term: 'Erfasste Zeiten',
              text: 'Zeiterfassung an diesem Ticket. Die Zahl am Reiter erscheint nur, wenn Sie Zeiten lesen dürfen.',
            },
            {
              term: 'Struktur',
              text: 'Teilaufgaben, Checklisten, Verknüpfungen, Über- und Untergeordnetes, Wiedervorlage. Die Zahl am Reiter zählt Teilaufgaben, Checklistenpunkte und Verknüpfungen zusammen.',
            },
            { term: 'Freigaben', text: 'Angeforderte und erteilte Genehmigungen.' },
            {
              term: 'Historie',
              text: 'Wer wann welches Feld von welchem auf welchen Wert geändert hat. Nicht änderbar. Dasselbe Akkordeon wie der Verlauf: zugeklappt stehen die geänderten Felder in einer Zeile.',
            },
          ],
        },
      ],
    },
    {
      id: 'sachverhalt',
      title: 'Der Sachverhalt',
      blocks: [
        {
          kind: 'text',
          text: 'Über dem Verlauf steht die Beschreibung — wofür der Vorgang läuft. Sie lässt sich im Reiter „Felder“ ändern und trägt seither dieselbe Auszeichnung wie ein Beitrag.',
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Bei einer Mail steht dort die Mail',
          text: 'Kommt ein Vorgang über das Postfach herein, füllt die Mail den Sachverhalt — mit ihrer Formatierung, nicht flachgeklopft. Der erste Beitrag darunter ist dieselbe Nachricht und startet deshalb zugeklappt: Er bleibt als Nachweis stehen, mit Absender, Empfängern und Anhängen, aber Sie lesen denselben Text nicht zweimal.',
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Ältere Vorgänge sehen aus wie bisher',
          text: 'Ein Vorgang aus der Zeit vor dem Editor und eine Anfrage aus dem Kundenportal tragen reinen Text. Der steht da, wie er hereinkam — mit seinen Zeilenumbrüchen. Er bekommt seine Auszeichnung, wenn ihn das nächste Mal jemand schreibt. Es wird nichts umgewandelt und nichts geraten.',
        },
      ],
    },
    {
      id: 'wartet-auf-uns',
      title: 'Wer wartet auf uns',
      blocks: [
        {
          kind: 'text',
          text: 'Schreibt jemand von außen — per E-Mail oder im Portal —, bekommt der Vorgang ein Kennzeichen: „Kundenantwort“, dazu ein farbiger Streifen an der Zeilenkante und die Spalte „Wartet“. Sie sagt, **seit wann** gewartet wird, nicht wann zuletzt geschrieben wurde. Schreibt jemand dreimal, zählt der erste — sonst rutschte ein Vorgang, der seit zwei Tagen liegt, durch eine Nachfrage nach unten.',
        },
        {
          kind: 'text',
          text: 'Der Schalter „Wartet auf uns“ über der Liste grenzt darauf ein. Er liegt quer zu den Zustandsreitern und lässt sich mit „Nur meine“ verbinden: meine offenen Vorgänge, bei denen jemand wartet. Auf der Startseite steht dieselbe Zahl als Kachel. Wer so jeden Morgen beginnt, speichert die Einschränkung als Ansicht — der Schalter wird mitgespeichert und beim Anwenden wieder gesetzt.',
        },
        {
          kind: 'note',
          tone: 'warning',
          title: 'Der Fall, um den es geht',
          text: 'Ein Vorgang ohne Bearbeiter, bei dem der Kunde seit zwei Tagen wartet. Er hat keinen Besitzer, taucht in keiner persönlichen Liste auf und stand in der nach Änderung sortierten Liste irgendwo in der Mitte. Genau diese rutschen durch — und genau sie stehen jetzt oben.',
        },
        {
          kind: 'terms',
          items: [
            {
              term: 'Was das Kennzeichen setzt',
              text: 'Eine eingegangene E-Mail, ein Portalbeitrag des Melders — und die Anlage eines Vorgangs, der von außen kam.',
            },
            {
              term: 'Was es wegnimmt',
              text: 'Eine Antwort an den Kunden. Lösen oder Schließen. Oder der Knopf „Ohne Antwort erledigt“.',
            },
            {
              term: 'Was es stehen lässt',
              text: 'Eine interne Notiz — der Kunde wartet weiter. Ein Entwurf — er ist nicht abgeschickt. Und eine Telefonnotiz: Sie hält genauso oft fest, dass der Kunde angerufen hat, wie dass wir es taten.',
            },
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          title: '„Ohne Antwort erledigt“',
          text: 'Für das „Danke!“, auf das man nicht antwortet. Der Knopf steht im Ticket über dem Schreibfeld und nimmt nur das Kennzeichen weg — der Vorgang bleibt offen, sein Status ändert sich nicht. Wer ihn abschließen will, löst oder schließt ihn; das nimmt das Kennzeichen ohnehin mit.',
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Im Ticket: die Linie im Verlauf',
          text: 'Über dem Schreibfeld steht, seit wann gewartet wird und wie viele Beiträge unbeantwortet sind. Im Verlauf darunter trennt eine Linie, was seit Ihrer letzten Antwort hereinkam; diese Beiträge tragen einen zusätzlichen Rahmen. Beides verschwindet, sobald eine Antwort hinausgeht.',
        },
      ],
    },
    {
      id: 'verlauf-lesen',
      title: 'Den Verlauf lesen',
      blocks: [
        {
          kind: 'shot',
          file: 'ticket-verlauf-liste',
          caption:
            'Der Verlauf: der neueste Beitrag offen, die älteren mit ihrer ersten Zeile. Jede Art trägt ihre eigene Farbe.',
        },
        {
          kind: 'text',
          text: 'Der neueste Beitrag steht oben und ist offen. Die älteren sind zugeklappt und zeigen Art, Verfasser, die erste Zeile und den Zeitpunkt — genug, um zu entscheiden, ob man sie öffnet. Ein Klick auf die Zeile klappt auf, ein zweiter wieder zu; „Alle ausklappen“ öffnet den ganzen Verlauf.',
        },
        {
          kind: 'text',
          text: 'Jede Art trägt Farbe, Sinnbild und Wort. Das ist dreifach gesagt, und mit Absicht: Wer Farben schlecht unterscheidet, verliert sonst genau die Unterscheidung, auf die es hier ankommt.',
        },
        {
          kind: 'terms',
          items: [
            { term: 'Antwort an den Kunden', text: 'Grün, Briefumschlag nach außen.' },
            { term: 'E-Mail eingehend', text: 'Blau, Briefumschlag nach innen.' },
            {
              term: 'Interne Notiz',
              text: 'Orange, Schloss — und als einzige Art ist die ganze Zeile eingefärbt. Das ist der Fall, in dem ein Irrtum den Kunden erreicht.',
            },
            { term: 'Telefonnotiz', text: 'Violett, Hörer.' },
            {
              term: 'System',
              text: 'Grau, Uhr, ohne Verfasser. Ein Systemvermerk ist kein Beitrag, sondern eine Randnotiz — er steht flacher und blasser da.',
            },
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Der Filter über dem Verlauf',
          text: '„Nur Kundenkontakt“ zeigt, was der Kunde geschrieben hat oder lesen kann — das ist die Ansicht, die Sie brauchen, wenn jemand fragt, was ihm mitgeteilt wurde. „Nur intern“ zeigt das Gegenstück. Systemvermerke fallen bei beiden heraus; sie sind weder das eine noch das andere.',
        },
      ],
    },
    {
      id: 'beitraege',
      title: 'Beiträge schreiben',
      blocks: [
        {
          kind: 'text',
          text: 'Ein Beitrag ist ein Eintrag im Verlauf. Die Art entscheidet, wer ihn sieht und ob er das Haus verlässt.',
        },
        {
          kind: 'terms',
          items: [
            {
              term: 'Interne Notiz',
              text: 'Bleibt intern. Erscheint nicht im Portal, geht an niemanden hinaus. Für Vermerke, Verdachtsmomente, Zwischenstände.',
            },
            {
              term: 'Antwort an den Kunden',
              text: 'Geht als E-Mail hinaus und erscheint im Portal. Anrede, Signatur und Portal-Link legt die Mailvorlage „Antwort an den Kunden (Rahmen)“ um Ihren Text.',
            },
            {
              term: 'Telefonnotiz',
              text: 'Was am Telefon besprochen wurde. Sichtbar, aber ohne Versand.',
            },
          ],
        },
        {
          kind: 'note',
          tone: 'danger',
          title: 'Die Sichtbarkeit filtert der Server',
          text: 'Eine interne Notiz wird im Portal nicht ausgeblendet — sie wird gar nicht erst ausgeliefert. Trotzdem gilt: Was Sie nicht in der Hand eines Kunden sehen wollen, gehört in eine interne Notiz und nicht in eine Antwort. Eine einmal versendete Antwort holt niemand zurück.',
        },
        {
          kind: 'steps',
          steps: [
            {
              text: 'Art des Beitrags wählen — Notiz, Antwort oder Telefonnotiz.',
              detail:
                'Mit der Tastatur geht das schneller: A springt in eine Antwort, N in eine interne Notiz — beides von überall im Ticket aus, sofern Sie gerade nicht in einem Eingabefeld stehen.',
            },
            {
              text: 'Bei einer Antwort die Empfänger prüfen — An, CC, BCC.',
              detail:
                'Vorbelegt sind der Ansprechpartner des Tickets und die Kopien der letzten Mail des Kunden. Wie das funktioniert, steht im nächsten Abschnitt.',
            },
            {
              text: 'Text schreiben — im Editor, wie in einem Textprogramm.',
              detail:
                'Fett, Kursiv, Unterstrichen, Durchgestrichen, Überschriften, Aufzählungen, Zitat, Codeblock, Link, Bild und Tabelle. Was Sie sehen, ist was ankommt: Der Editor kann nur erzeugen, was das System auch speichert. Derselbe Editor steht in jeder Maske der Anwendung — Wissensartikel, Mailvorlage, Signatur, Ankündigung, Textbaustein.',
            },
            {
              text: 'Textbaustein einfügen, wenn es einen passenden gibt.',
              detail:
                'Bausteine legt die Verwaltung an; sie haben Kürzel und lassen sich über das Auswahlfeld holen.',
            },
            {
              text: 'Dateien anhängen, falls nötig — auch durch Hineinziehen in die gestrichelte Fläche.',
              detail:
                'Ein Bild im Text geht denselben Weg: Der Bildknopf lädt es als Anhang hoch und bindet es ein. Bilder von fremden Servern nimmt das System nicht an — sie wären Zählpixel.',
            },
            {
              text: '„Als Entwurf sichern“ oder „Absenden“ — oder Strg+↵ im Textfeld.',
              detail:
                'Ein Entwurf bleibt am Ticket stehen und geht an niemanden. Er ist für Kollegen sichtbar und steht im Protokoll. Die Eingabetaste allein bleibt der Zeilenumbruch.',
            },
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Unversendeter Text geht nicht verloren',
          text: 'Was Sie tippen, hält Ihr Browser fest — je Ticket und je Beitragsart getrennt. Schließen Sie den Reiter versehentlich oder drücken Sie zurück, steht der halb geschriebene Text beim nächsten Öffnen wieder da. Er verschwindet, sobald der Beitrag abgeschickt ist, und spätestens bei der Abmeldung: Ein Beitragstext kann personenbezogene Daten enthalten und hat an einem geteilten Arbeitsplatz danach nichts mehr zu suchen.',
        },
        {
          kind: 'note',
          tone: 'warning',
          title: 'Das ist nicht der Entwurf am Ticket',
          text: 'Der geretteten Text liegt allein in Ihrem Browser. Ein Kollege sieht ihn nicht, ein zweites Gerät auch nicht, und im Protokoll steht er nicht. Wer einen Zwischenstand festhalten will, den andere sehen sollen, nimmt „Als Entwurf sichern“.',
        },
      ],
    },
    {
      id: 'empfaenger',
      title: 'Empfänger einer Antwort: An, CC, BCC',
      blocks: [
        {
          kind: 'shot',
          file: 'antwort-empfaenger',
          caption:
            'Die Antwort mit vorbelegtem „An“ und der Kopie aus der letzten Mail des Kunden.',
        },
        {
          kind: 'text',
          text: 'Wählen Sie „Antwort an den Kunden“, erscheinen über dem Text drei Empfängerzeilen. Jede Adresse steht als Kachel darin; das × entfernt sie.',
        },
        {
          kind: 'terms',
          items: [
            {
              term: 'An',
              text: 'Vorbelegt mit dem Ansprechpartner des Tickets. Lässt sich ändern — etwa wenn inzwischen jemand anderes beim Kunden zuständig ist. Ohne Adresse unter „An“ lässt sich die Antwort nicht absenden.',
            },
            {
              term: 'CC',
              text: 'Vorbelegt mit den Kopien der letzten Mail, die der Kunde geschickt hat — wer mit Kollegen in Kopie schreibt, erwartet die Antwort an alle. Die eigenen Postfächer des Systems stehen nicht darin. Was nicht hineingehört, entfernen Sie.',
            },
            {
              term: 'BCC',
              text: 'Blindkopie: Die Empfänger bekommen die Mail, die übrigen sehen es nicht. Im Verlauf steht die Blindkopie für Bearbeiter, im Portal und im Kopf der Mail nicht.',
            },
          ],
        },
        {
          kind: 'steps',
          steps: [
            {
              text: 'In eine Zeile klicken — vorgeschlagen werden die Ansprechpartner des Kunden, dem das Ticket zugeordnet ist.',
              detail:
                'Nur dieses Kunden: Eine Liste aller Kontakte lüde dazu ein, die Antwort an den falschen Kunden in Kopie zu setzen. Ansprechpartner ohne E-Mail-Adresse und anonymisierte erscheinen nicht.',
            },
            {
              text: 'Oder eine Adresse eintippen und mit der Eingabetaste übernehmen.',
              detail:
                'Auch eine Adresse, die nicht im CRM steht. Eine getippte Adresse gilt auch ohne Eingabetaste, sobald Sie absenden. Ist sie unvollständig, sagt das Feld es, und „Antwort senden“ bleibt gesperrt.',
            },
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Jede Adresse steht nur einmal',
          text: 'Steht jemand in zwei Zeilen, gilt die sichtbarere: „An“ vor CC vor BCC. Wer in „An“ steht und zugleich in BCC, bekäme die Mail sonst zweimal — und wüsste aus der zweiten, dass er blind mitliest. Mehr als zwanzig Adressen je Zeile nimmt das System nicht an: Das wäre ein Rundschreiben und kein Vorgang.',
        },
      ],
    },
    {
      id: 'anhaenge',
      title: 'Anhänge',
      blocks: [
        {
          kind: 'text',
          text: 'Dateien hängen am Beitrag und erscheinen zusätzlich gesammelt im Reiter „Anhänge“. Ein Anhang wird immer als Download ausgeliefert und nie im Browser dargestellt — auch eine HTML-Datei nicht.',
        },
        {
          kind: 'note',
          tone: 'warning',
          title: '„nicht auf Schadsoftware geprüft“',
          text: 'Steht dieser Hinweis an einem Anhang, war beim Hochladen kein Prüfdienst erreichbar. Die Datei ist da, aber ungeprüft. Behandeln Sie sie wie eine Mail von einem Unbekannten.',
        },
      ],
    },
    {
      id: 'abschluss',
      title: 'Übernehmen, lösen, schließen, wieder öffnen',
      blocks: [
        {
          kind: 'steps',
          steps: [
            {
              text: '„Ticket übernehmen“ setzt Sie als Bearbeiter ein.',
              detail:
                'Ein Klick statt eines Wechsels in den Reiter „Felder“. Der häufigste Handgriff des Tages soll der kürzeste sein.',
            },
            {
              text: '„Als gelöst melden“, wenn die Sache fachlich erledigt ist.',
              detail:
                'Der Kunde bekommt die Nachricht und die Zufriedenheitsumfrage. Er kann widersprechen.',
            },
            {
              text: '„Schließen“, wenn nichts mehr kommt.',
              detail:
                'Ein Ticket mit untergeordneten Tickets lässt sich erst schließen, wenn alle untergeordneten geschlossen sind — außer es ist als Großstörung gekennzeichnet, dann gibt es seinen Abschluss an sie weiter.',
            },
            {
              text: '„Wieder öffnen“, wenn der Kunde zurückkommt.',
              detail:
                'Die Frist läuft dann weiter. Ein neues Ticket wäre der falsche Weg — es zerrisse die Vorgeschichte.',
            },
          ],
        },
      ],
    },
    {
      id: 'massenbearbeitung',
      title: 'Massenbearbeitung',
      blocks: [
        {
          kind: 'text',
          text: 'In der Liste lassen sich mehrere Tickets ankreuzen und gemeinsam ändern: Warteschlange, Priorität, Bearbeiter, Kategorie. Der übliche Anlass ist eine Umschichtung — ein Kollege geht in Urlaub, eine Warteschlange wird geteilt.',
        },
        {
          kind: 'note',
          tone: 'warning',
          title: 'Jede Änderung steht im Protokoll',
          text: 'Auch die aus der Massenbearbeitung — je Ticket ein Eintrag mit altem und neuem Wert. Ein Fehlgriff ist nachvollziehbar, aber nicht mit einem Klick rückgängig zu machen.',
        },
      ],
    },
    {
      id: 'weiteres',
      title: 'Beobachten, PDF, gleichzeitiges Arbeiten',
      blocks: [
        {
          kind: 'terms',
          items: [
            {
              term: 'Beobachten',
              text: 'Sie werden über Änderungen benachrichtigt, ohne Bearbeiter zu sein. Für Tickets, die Sie angehen, aber nicht führen.',
            },
            {
              term: 'Als PDF',
              text: 'Druckt das Ticket samt Verlauf. Interne Notizen bleiben draußen, außer Sie kreuzen es ausdrücklich an. Die Datei landet im Download-Ordner Ihres Browsers. Kann der Server sie nicht erzeugen, erscheint über dem Verlauf eine Fehlermeldung mit Fehlerkennung — geben Sie diese an, wenn Sie den Fehler melden.',
            },
            {
              term: 'Ein Kollege ist schon dran',
              text: 'Öffnet ein zweiter Bearbeiter dasselbe Ticket, erscheint ein Hinweis. Speichern bleibt möglich — beim Konflikt meldet der Server den aktuellen Stand, statt Ihre Eingabe stillschweigend zu überschreiben.',
            },
          ],
        },
      ],
    },
  ],
};

const struktur: HandbookChapter = {
  id: 'ticket-struktur',
  title: 'Tickets ordnen',
  summary: 'Teilaufgaben, Checklisten, Verknüpfungen, Großstörung, Zusammenführen und Aufteilen.',
  audience: 'agent',
  route: '/app/tickets',
  sections: [
    {
      id: 'teilaufgaben',
      title: 'Teilaufgaben und Checklisten',
      blocks: [
        {
          kind: 'shot',
          file: 'ticket-struktur',
          caption: 'Der Reiter „Struktur“ mit Teilaufgaben, Checkliste und Verknüpfungen.',
        },
        {
          kind: 'text',
          text: 'Eine Teilaufgabe ist ein Arbeitsschritt innerhalb des Tickets, mit eigener Zuständigkeit und eigenem Zustand. Sie kann auf eine andere Teilaufgabe warten — dann steht am Eintrag, worauf.',
        },
        {
          kind: 'text',
          text: 'Eine Checkliste ist die leichtere Form: Punkte zum Abhaken, ohne Zuständigkeit. Für wiederkehrende Abläufe legt die Verwaltung Checklistenvorlagen an — der Einrichtungsablauf für einen neuen Mitarbeiter etwa.',
        },
        {
          kind: 'steps',
          steps: [
            { text: 'Am Ticket den Reiter „Struktur“ öffnen.' },
            {
              text: 'Unter „Checklisten“ bei „Aus einer Vorlage“ die Vorlage wählen.',
              detail:
                'Neben dem Namen steht die Zahl der Punkte — bei zwei ähnlichen Vorlagen erkennt man daran, welche gemeint ist.',
            },
            { text: '„Vorlage anhängen“ klicken.' },
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Die Punkte werden kopiert, nicht verknüpft',
          text: 'Wer die Vorlage später ändert, ändert keine laufende Abarbeitung. Sonst verschwände ein bereits gesetzter Haken, weil jemand die Vorlage angepasst hat. Umgekehrt heißt das: Eine Korrektur an der Vorlage wirkt erst auf die nächste Checkliste.',
        },
        {
          kind: 'text',
          text: 'Ohne Vorlage geht es auch — darunter lassen sich Name und Punkte von Hand eintragen, ein Punkt je Zeile.',
        },
        {
          kind: 'route',
          to: '/app/administration/checklist-templates',
          label: 'Zu den Checklistenvorlagen',
        },
      ],
    },
    {
      id: 'verknuepfungen',
      title: 'Verknüpfungen und Hierarchie',
      blocks: [
        {
          kind: 'text',
          text: 'Zwei Tickets lassen sich verknüpfen — als Duplikat, als verwandt, als blockierend. Die Verknüpfung erscheint auf beiden Seiten, auf der Gegenseite als „umgekehrt“. Das Zielticket suchen Sie über einen Teil seiner Nummer oder seines Betreffs, ebenso das übergeordnete Ticket.',
        },
        {
          kind: 'text',
          text: 'Davon zu unterscheiden ist die Hierarchie: ein übergeordnetes Ticket mit untergeordneten. Das übergeordnete lässt sich erst schließen, wenn alle untergeordneten geschlossen sind.',
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Großstörung',
          text: 'Ist ein übergeordnetes Ticket als Großstörung gekennzeichnet, kehrt sich die Regel um: Es gibt seinen Abschluss an alle untergeordneten weiter. Der Anwendungsfall ist der Leitungsausfall, zu dem sechzig Einzelmeldungen eingehen — Sie lösen eines und schließen alle.',
        },
      ],
    },
    {
      id: 'zuschnitt',
      title: 'Zusammenführen und Aufteilen',
      blocks: [
        {
          kind: 'steps',
          steps: [
            { text: 'Am Ticket „Zusammenführen oder aufteilen“ öffnen.' },
            {
              text: 'Zum Zusammenführen das Zielticket im Feld „Zielticket“ suchen und wählen.',
              detail:
                'Ein Teil der Nummer oder des Betreffs genügt — „57“ findet T-2026-000057, „drucker“ den Drucker am Empfang. Kunde und Stand stehen in jeder Zeile daneben; das Ticket, an dem Sie gerade stehen, erscheint nicht. Beiträge und Anhänge wandern in das Zielticket. Das Quellticket wird geschlossen und mit dem Ziel verknüpft — es bleibt bestehen, denn seine Nummer steht bereits in der Kundenkommunikation.',
            },
            {
              text: 'Zum Aufteilen die Beiträge ankreuzen, die herauswandern sollen, und einen Betreff für das neue Ticket vergeben.',
              detail:
                'Beide Tickets bleiben verknüpft. Der übliche Anlass: In einer Mail stehen zwei Anliegen, und sie gehören verschiedenen Warteschlangen.',
            },
          ],
        },
      ],
    },
    {
      id: 'wiedervorlage',
      title: 'Wiedervorlage',
      blocks: [
        {
          kind: 'text',
          text: 'Ein Ticket auf Wiedervorlage legen heißt: Es soll an einem bestimmten Tag wieder auf den Tisch. Dazu gehört ein Vermerk, worum es dann geht — ein Datum ohne Vermerk ist in drei Wochen wertlos.',
        },
      ],
    },
  ],
};

const board: HandbookChapter = {
  id: 'board',
  title: 'Kanban-Board',
  summary: 'Dieselben Tickets als Spalten je Status — für den Blick auf den Fluss.',
  audience: 'agent',
  route: '/app/board',
  sections: [
    {
      id: 'board',
      title: 'Das Board',
      blocks: [
        {
          kind: 'shot',
          file: 'board',
          caption: 'Eine Spalte je Status, gefiltert nach Warteschlange.',
        },
        {
          kind: 'text',
          text: 'Das Board ist eine zweite Sicht auf dieselbe Ticketliste, kein eigener Bestand. Eine Spalte je Status, oben die Anzahl. Es beantwortet eine andere Frage als die Liste: nicht „was muss ich als Nächstes tun“, sondern „wo staut es sich“.',
        },
        {
          kind: 'text',
          text: 'Die Auswahl oben grenzt auf eine Warteschlange ein. Das Board hat eine eigene Adresse und lässt sich als Lesezeichen behalten — wer so arbeitet, arbeitet den ganzen Tag darin.',
        },
        {
          kind: 'text',
          text: 'Darüber steht, wie in der Liste, das Feld „Kunde“. Ein dort gewählter Kunde gilt für die Karten und für die Zahl an jeder Spalte. Wer in der Liste einen Kunden gewählt hat und von dort ins Board wechselt, nimmt ihn mit — und ebenso zurück.',
        },
        { kind: 'route', to: '/app/board', label: 'Zum Board' },
      ],
    },
  ],
};

export const ticketChapters: HandbookChapter[] = [grundlagen, bearbeiten, struktur, board];
